/**
 * Poster birth chart — art-directed SVG natal wheel for large-format print.
 *
 * Sibling of tools/birth_chart_fe/birthChartWheel.js. Same chart-data contract
 * and longitude maths; different layout (rotated signs/houses, graduated house
 * band, monochrome aspects at 8° orb, clipped sky field).
 *
 * API:
 *   renderPosterBirthChart(container, chartData, options) -> SVGSVGElement
 *
 * options:
 *   iconsBaseUrl, signIconsBaseUrl, iconsCacheBust
 *   sky: exact-birth payload from POST /poster or /analyze (preferred)
 *   skyBaseUrl, showSky, skySign (0–11 or "aries"…)  // R2 fallback when sky is absent
 *   skyParallax (default true when interactive): far + near plates
 *   skyParallaxShift (SVG units at depth 1, default 14)
 *   ink, paper
 *   style: "light" | "dark" | "mix"  (mix = dark sky/aspects, light outer rings)
 *   innerInk, innerPaper  (mix only; defaults navy + cream)
 *   interactive (hover grow + sky parallax)
 *   aspectOrb (default 8)
 *   includeAngleAspects (default true)
 *   inlineIcons (same-origin only; falls back to invert)
 *
 * Matplotlib is y-up; SVG is y-down. Geometry is computed in matplotlib space,
 * then mapped with ySvg = canvasSize - y. Planet/AC/MC icons stay upright;
 * sign and house glyphs rotate so their top points radially outward.
 */
(function (global) {
  "use strict";

  const NS = "http://www.w3.org/2000/svg";
  const XLINK = "http://www.w3.org/1999/xlink";

  const canvasSize = 1200;
  const center = { x: 600, y: 600 };
  const R = 560;

  // Outside-in: sign glyphs, bold segmented sign ring, house discs, thin
  // graduated house band, planets just outside the constellation rim, sky.
  const signGlyphRadius = R * 0.923;
  const signBoldRadius = R * 0.846;
  const signBoldStroke = 6.2;
  const signGapDeg = 0.7;

  const houseRingOuter = R * 0.798;
  const houseRingInner = R * 0.78;
  const houseDiscRadius = 15.5;
  const houseIconRadius = (houseRingInner + houseRingOuter) / 2.0;

  const aspectHubRadius = R * 0.64;
  const planetTickLen = R * 0.013;
  const planetIconRadius = aspectHubRadius + planetTickLen + R * 0.054;
  const skyClipRadius = aspectHubRadius;
  const skyPad = 40;
  const skyFarDepth = 0.35;
  const skyNearDepth = 1;
  const skyParallaxShiftDefault = 14;
  const nodeRadiusPx = 2.1;

  const canvasRatio = canvasSize / 1500.0;
  const dpiCompensation = 100.0 / 72.0;
  const signIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 1.32); // ~70
  const houseIconSize = 24;
  const planetIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 1.12); // ~59
  const angleIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 0.72); // ~38

  const SIGNS = [
    "ARIES",
    "TAURUS",
    "GEMINI",
    "CANCER",
    "LEO",
    "VIRGO",
    "LIBRA",
    "SCORPIO",
    "SAGITTARIUS",
    "CAPRICORN",
    "AQUARIUS",
    "PISCES",
  ];
  const SIGNS_LOWER = SIGNS.map((s) => s.toLowerCase());

  const PLANET_WEIGHT = "BOLD";
  const SIGN_WEIGHT = "HEAVY";
  const ICON_FILES = {
    Sun: `PLANETS/SUN_${PLANET_WEIGHT}.svg`,
    Moon: `PLANETS/MOON_${PLANET_WEIGHT}.svg`,
    Mercury: `PLANETS/MERCURY_${PLANET_WEIGHT}.svg`,
    Venus: `PLANETS/VENUS_${PLANET_WEIGHT}.svg`,
    Mars: `PLANETS/MARS_${PLANET_WEIGHT}.svg`,
    Jupiter: `PLANETS/JUPITER_${PLANET_WEIGHT}.svg`,
    Saturn: `PLANETS/SATURN_${PLANET_WEIGHT}.svg`,
    Uranus: `PLANETS/URANUS_${PLANET_WEIGHT}.svg`,
    Neptune: `PLANETS/NEPTUNE_${PLANET_WEIGHT}.svg`,
    Pluto: `PLANETS/PLUTO_${PLANET_WEIGHT}.svg`,
    "North Node": `PLANETS/NORTH_NODE_${PLANET_WEIGHT}.svg`,
    Ascendant: "PLANETS/AC_S90.svg",
    MC: "PLANETS/MC_S90.svg",
    ARIES: `ASTRO SIGNS/ARIES_${SIGN_WEIGHT}.svg`,
    TAURUS: `ASTRO SIGNS/TAURUS_${SIGN_WEIGHT}.svg`,
    GEMINI: `ASTRO SIGNS/GEMINI_${SIGN_WEIGHT}.svg`,
    CANCER: `ASTRO SIGNS/CANCER_${SIGN_WEIGHT}.svg`,
    LEO: `ASTRO SIGNS/LEO_${SIGN_WEIGHT}.svg`,
    VIRGO: `ASTRO SIGNS/VIRGO_${SIGN_WEIGHT}.svg`,
    LIBRA: `ASTRO SIGNS/LIBRA_${SIGN_WEIGHT}.svg`,
    SCORPIO: `ASTRO SIGNS/SCORPIO_${SIGN_WEIGHT}.svg`,
    SAGITTARIUS: `ASTRO SIGNS/SAGITTARIUS_${SIGN_WEIGHT}.svg`,
    CAPRICORN: `ASTRO SIGNS/CAPRICORN_${SIGN_WEIGHT}.svg`,
    AQUARIUS: `ASTRO SIGNS/AQUARIUS_${SIGN_WEIGHT}.svg`,
    PISCES: `ASTRO SIGNS/PISCES_${SIGN_WEIGHT}.svg`,
    1: "HOUSES/HOUSE_01.svg",
    2: "HOUSES/HOUSE_02.svg",
    3: "HOUSES/HOUSE_03.svg",
    4: "HOUSES/HOUSE_04.svg",
    5: "HOUSES/HOUSE_05.svg",
    6: "HOUSES/HOUSE_06.svg",
    7: "HOUSES/HOUSE_07.svg",
    8: "HOUSES/HOUSE_08.svg",
    9: "HOUSES/HOUSE_09.svg",
    10: "HOUSES/HOUSE_10.svg",
    11: "HOUSES/HOUSE_11.svg",
    12: "HOUSES/HOUSE_12.svg",
  };

  const ASPECT_ANGLES = {
    conjunction: 0,
    sextile: 60,
    square: 90,
    trine: 120,
    opposition: 180,
  };
  const SOLID_ASPECTS = { conjunction: true, sextile: true, trine: true };

  /**
   * Circumferential spacing weights from glyph WIDTH × display size.
   * Copied from birthChartWheel.js — 1.0 = North Node.
   */
  const ICON_SPACING_WEIGHT = {
    "North Node": 1.0,
    Uranus: 0.962,
    MC: 0.957,
    Mars: 0.948,
    Ascendant: 0.944,
    Sun: 0.937,
    Jupiter: 0.908,
    Pluto: 0.876,
    Neptune: 0.872,
    Moon: 0.852,
    Venus: 0.847,
    Mercury: 0.84,
    Saturn: 0.84,
  };

  let instanceId = 0;

  function spacingWeight(name) {
    const w = ICON_SPACING_WEIGHT[name];
    return w == null ? 1.0 : w;
  }

  function el(tag, attrs, parent) {
    const node = document.createElementNS(NS, tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (v === undefined || v === null) continue;
        if (k === "href") {
          node.setAttributeNS(XLINK, "href", v);
          node.setAttribute("href", v);
        } else {
          node.setAttribute(k, String(v));
        }
      }
    }
    if (parent) parent.appendChild(node);
    return node;
  }

  function toSvg(x, y) {
    return { x, y: canvasSize - y };
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  function longitudeToAngle(longitude, ascendantLongitude) {
    if (ascendantLongitude != null) {
      const thetaDeg = mod(180 + (longitude - ascendantLongitude), 360);
      return (thetaDeg * Math.PI) / 180;
    }
    return (mod(180 + longitude, 360) * Math.PI) / 180;
  }

  function angleToPosition(angle, radius) {
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    };
  }

  function planetGeo(longitude, ascendantLongitude) {
    const angle = longitudeToAngle(longitude, ascendantLongitude);
    const hub = angleToPosition(angle, aspectHubRadius);
    const tick = angleToPosition(angle, aspectHubRadius + planetTickLen);
    const icon = angleToPosition(angle, planetIconRadius);
    return {
      on: { x: hub.x + center.x, y: hub.y + center.y },
      tick: { x: tick.x + center.x, y: tick.y + center.y },
      icon: { x: icon.x + center.x, y: icon.y + center.y },
      hub: { x: hub.x + center.x, y: hub.y + center.y },
      angle,
    };
  }

  function argsort(arr) {
    return arr
      .map((v, i) => [v, i])
      .sort((a, b) => a[0] - b[0])
      .map((p) => p[1]);
  }

  function unwrap(angles) {
    const out = angles.slice();
    for (let i = 1; i < out.length; i++) {
      let d = out[i] - out[i - 1];
      while (d > Math.PI) {
        out[i] -= 2 * Math.PI;
        d = out[i] - out[i - 1];
      }
      while (d < -Math.PI) {
        out[i] += 2 * Math.PI;
        d = out[i] - out[i - 1];
      }
    }
    return out;
  }

  /** Pool-Adjacent-Violators — mirror renderer._compute_icon_angle_offsets.pav */
  function pav(y) {
    const n = y.length;
    const v = y.slice();
    const w = Array(n).fill(1.0);
    const i0 = Array.from({ length: n }, (_, i) => i);
    const i1 = Array.from({ length: n }, (_, i) => i);
    let k = 0;
    while (k < v.length - 1) {
      if (v[k] <= v[k + 1]) {
        k += 1;
        continue;
      }
      const nv = (w[k] * v[k] + w[k + 1] * v[k + 1]) / (w[k] + w[k + 1]);
      const nw = w[k] + w[k + 1];
      v[k] = nv;
      w[k] = nw;
      i1[k] = i1[k + 1];
      v.splice(k + 1, 1);
      w.splice(k + 1, 1);
      i0.splice(k + 1, 1);
      i1.splice(k + 1, 1);
      while (k > 0 && v[k - 1] > v[k]) {
        const nv2 = (w[k - 1] * v[k - 1] + w[k] * v[k]) / (w[k - 1] + w[k]);
        const nw2 = w[k - 1] + w[k];
        v[k - 1] = nv2;
        w[k - 1] = nw2;
        i1[k - 1] = i1[k];
        v.splice(k, 1);
        w.splice(k, 1);
        i0.splice(k, 1);
        i1.splice(k, 1);
        k -= 1;
      }
    }
    const out = new Array(n);
    for (let bi = 0; bi < v.length; bi++) {
      for (let j = i0[bi]; j <= i1[bi]; j++) out[j] = v[bi];
    }
    return out;
  }

  function computeIconAngleOffsets(planetPositions, ascendantLongitude, angles) {
    const bodies = [];
    for (const [name, data] of Object.entries(planetPositions || {})) {
      bodies.push([name, longitudeToAngle(data.longitude, ascendantLongitude)]);
    }
    if (angles) {
      for (const [name, data] of Object.entries(angles)) {
        bodies.push([name, longitudeToAngle(data.longitude, ascendantLongitude)]);
      }
    }
    if (!bodies.length) return {};

    const names = bodies.map((b) => b[0]);
    const aDes = bodies.map((b) => b[1]);

    const r = Math.max(planetIconRadius, 1.0);
    const iconPx = planetIconSize;
    const haloPx = 1.5;
    const safety = 1.05;
    const gapScale = 1.3;

    function halfRadFor(name) {
      const w = spacingWeight(name);
      const halfW = 0.5 * iconPx * w + haloPx;
      return Math.atan(halfW / r) * safety;
    }

    const order = argsort(aDes);
    const aSorted = order.map((i) => aDes[i]);
    const namesSorted = order.map((i) => names[i]);

    const gaps = [];
    for (let i = 0; i < aSorted.length; i++) {
      const next = i + 1 < aSorted.length ? aSorted[i + 1] : aSorted[0] + 2 * Math.PI;
      gaps.push(next - aSorted[i]);
    }
    let kBreak = 0;
    for (let i = 1; i < gaps.length; i++) {
      if (gaps[i] > gaps[kBreak]) kBreak = i;
    }

    const aRot = aSorted.slice(kBreak + 1).concat(aSorted.slice(0, kBreak + 1));
    const namesRot = namesSorted.slice(kBreak + 1).concat(namesSorted.slice(0, kBreak + 1));
    const aUnwrap = unwrap(aRot);

    const s = new Array(aUnwrap.length).fill(0);
    for (let i = 1; i < s.length; i++) {
      s[i] =
        (gapScale / 2) * (halfRadFor(namesRot[i - 1]) + halfRadFor(namesRot[i]));
    }
    const G = [];
    let acc = 0;
    for (let i = 0; i < s.length; i++) {
      acc += s[i];
      G.push(acc);
    }
    const u = aUnwrap.map((a, i) => a - G[i]);
    const uStar = pav(u);
    const y = uStar.map((val, i) => val + G[i]);

    const finalAngles = {};
    for (let i = 0; i < namesRot.length; i++) finalAngles[namesRot[i]] = y[i];

    const offsets = {};
    for (let i = 0; i < names.length; i++) {
      const n = names[i];
      const a0 = aDes[i];
      const af = finalAngles[n];
      const dth = mod(af - a0 + Math.PI, 2 * Math.PI) - Math.PI;
      offsets[n] = dth;
    }
    return offsets;
  }

  function iconUrl(baseUrl, name, cacheBust) {
    const file = ICON_FILES[name];
    if (!file) return null;
    const base = String(baseUrl || "").replace(/\/?$/, "/");
    const encoded = file
      .split("/")
      .map((seg) => encodeURIComponent(seg))
      .join("/");
    let url = base + encoded;
    if (cacheBust) {
      url +=
        (url.includes("?") ? "&" : "?") +
        "v=" +
        encodeURIComponent(String(cacheBust));
    }
    return url;
  }

  function outwardRotDeg(svgX, svgY) {
    return (Math.atan2(svgY - center.y, svgX - center.x) * 180) / Math.PI + 90;
  }

  function isLightColor(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return false;
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 160;
  }

  function hexToRgb(hex) {
    const raw = String(hex || "#000000").replace("#", "").trim();
    const c =
      raw.length === 3
        ? raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2]
        : raw.slice(0, 6);
    if (c.length < 6) return null;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return { r, g, b };
  }

  function angularDistance(lon1, lon2) {
    let d = Math.abs(lon1 - lon2);
    if (d > 180) d = 360 - d;
    return d;
  }

  function collectBodies(chartData, includeAngleAspects) {
    const out = {};
    const skipAngles = includeAngleAspects === false;
    for (const [name, data] of Object.entries(chartData.planet_positions || {})) {
      if (skipAngles && (name === "Ascendant" || name === "MC")) continue;
      if (data && data.longitude != null) out[name] = data;
    }
    if (!skipAngles) {
      for (const [name, data] of Object.entries(chartData.angles || {})) {
        if (data && data.longitude != null) out[name] = data;
      }
    }
    return out;
  }

  function computePosterAspects(bodies, orb) {
    const names = Object.keys(bodies);
    const aspects = [];
    for (let i = 0; i < names.length; i++) {
      for (let j = i + 1; j < names.length; j++) {
        const dist = angularDistance(
          bodies[names[i]].longitude,
          bodies[names[j]].longitude
        );
        for (const [aspect, target] of Object.entries(ASPECT_ANGLES)) {
          if (Math.abs(dist - target) <= orb) {
            aspects.push({
              body1: names[i],
              body2: names[j],
              aspect,
              dashed: !SOLID_ASPECTS[aspect],
              orb: Math.abs(dist - target),
            });
            break;
          }
        }
      }
    }
    return aspects;
  }

  function resolveSkyIndex(chartData, skySign) {
    if (skySign != null && skySign !== "") {
      if (typeof skySign === "number" && Number.isFinite(skySign)) {
        return mod(Math.floor(skySign), 12);
      }
      const key = String(skySign).trim().toLowerCase();
      const named = SIGNS_LOWER.indexOf(key);
      if (named >= 0) return named;
      const asNum = Number(key);
      if (Number.isFinite(asNum)) return mod(Math.floor(asNum), 12);
    }
    const pp = chartData.planet_positions || {};
    const mc =
      (pp.MC && pp.MC.longitude) ||
      (chartData.angles && chartData.angles.MC && chartData.angles.MC.longitude);
    if (mc == null) return 0;
    return mod(Math.floor(mc / 30), 12);
  }

  function skyStem(idx) {
    return `sky-mc-${String(idx + 1).padStart(2, "0")}-${SIGNS_LOWER[idx]}`;
  }

  function skyFileName(idx, plate) {
    const stem = skyStem(idx);
    if (plate === "far" || plate === "near") return `${stem}-${plate}.svg`;
    return `${stem}.svg`;
  }

  function placeIcon(parent, baseUrl, name, xMpl, yMpl, size, cacheBust, rotateOutward, interactive, hitR, hitClass) {
    const url = iconUrl(baseUrl, name, cacheBust);
    const p = toSvg(xMpl, yMpl);
    if (!url) {
      el("circle", { cx: p.x, cy: p.y, r: 6, fill: "currentColor" }, parent);
      return;
    }
    if (!interactive) {
      const attrs = {
        href: url,
        x: p.x - size / 2,
        y: p.y - size / 2,
        width: size,
        height: size,
        preserveAspectRatio: "xMidYMid meet",
        "data-icon": String(name),
      };
      if (rotateOutward) {
        attrs.transform = `rotate(${outwardRotDeg(p.x, p.y)} ${p.x} ${p.y})`;
      }
      el("image", attrs, parent);
      return;
    }
    const rot = rotateOutward ? outwardRotDeg(p.x, p.y) : 0;
    const hit = el(
      "g",
      { class: `poster-hit${hitClass ? " " + hitClass : ""}`, "data-icon": String(name) },
      parent
    );
    el(
      "circle",
      {
        class: "hit-area",
        cx: p.x,
        cy: p.y,
        r: hitR || size * 0.62,
        fill: "#fff",
        "fill-opacity": 0,
      },
      hit
    );
    const placed = el(
      "g",
      { transform: `translate(${p.x} ${p.y}) rotate(${rot})` },
      hit
    );
    const icon = el("g", { class: "poster-icon" }, placed);
    el(
      "image",
      {
        href: url,
        x: -size / 2,
        y: -size / 2,
        width: size,
        height: size,
        preserveAspectRatio: "xMidYMid meet",
      },
      icon
    );
    return hit;
  }

  function annulusSectorPath(lon0, lon1, rInner, rOuter, ascendantLongitude) {
    const outer0 = polarSvg(lon0, rOuter, ascendantLongitude);
    const outer1 = polarSvg(lon1, rOuter, ascendantLongitude);
    const inner0 = polarSvg(lon0, rInner, ascendantLongitude);
    const inner1 = polarSvg(lon1, rInner, ascendantLongitude);
    return (
      `M ${outer0.x} ${outer0.y} ` +
      `A ${rOuter} ${rOuter} 0 0 0 ${outer1.x} ${outer1.y} ` +
      `L ${inner1.x} ${inner1.y} ` +
      `A ${rInner} ${rInner} 0 0 1 ${inner0.x} ${inner0.y} Z`
    );
  }

  function injectInteractiveCss(svg) {
    const style = document.createElementNS(NS, "style");
    style.textContent = [
      ".poster-hit { cursor: pointer; }",
      ".poster-hit .hit-area { pointer-events: all; }",
      ".poster-icon {",
      "  transform-box: fill-box;",
      "  transform-origin: center;",
      "  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);",
      "}",
      ".poster-hit:hover .poster-icon { transform: scale(1.42); }",
      ".poster-sky-shift { will-change: transform; }",
      "@media (prefers-reduced-motion: reduce) {",
      "  .poster-sky-shift { transform: none !important; }",
      "}",
    ].join("\n");
    svg.insertBefore(style, svg.firstChild);
  }

  function attachParallax(svg, layers, maxShift) {
    if (!layers || !layers.length) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const strength = maxShift == null ? skyParallaxShiftDefault : maxShift;
    const current = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let raf = 0;
    function apply(x, y) {
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const d = layer.depth == null ? 1 : layer.depth;
        layer.el.setAttribute("transform", `translate(${x * d} ${y * d})`);
      }
    }
    function tick() {
      current.x += (target.x - current.x) * 0.14;
      current.y += (target.y - current.y) * 0.14;
      apply(current.x, current.y);
      if (
        Math.abs(target.x - current.x) > 0.04 ||
        Math.abs(target.y - current.y) > 0.04
      ) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = 0;
      }
    }
    function kick() {
      if (!raf) raf = requestAnimationFrame(tick);
    }
    svg.addEventListener("pointermove", function (ev) {
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const nx = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((ev.clientY - rect.top) / rect.height) * 2 - 1;
      target.x = Math.max(-1, Math.min(1, nx)) * -strength;
      target.y = Math.max(-1, Math.min(1, ny)) * -strength;
      kick();
    });
    svg.addEventListener("pointerleave", function () {
      target.x = 0;
      target.y = 0;
      kick();
    });
  }

  function lineMpl(svg, x1, y1, x2, y2, attrs) {
    const a = toSvg(x1, y1);
    const b = toSvg(x2, y2);
    el(
      "line",
      {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        ...attrs,
      },
      svg
    );
  }

  function circleMpl(svg, cx, cy, r, attrs) {
    const p = toSvg(cx, cy);
    el(
      "circle",
      {
        cx: p.x,
        cy: p.y,
        r,
        ...attrs,
      },
      svg
    );
  }

  function polarSvg(longitude, radius, ascendantLongitude) {
    const angle = longitudeToAngle(longitude, ascendantLongitude);
    const p = angleToPosition(angle, radius);
    return toSvg(p.x + center.x, p.y + center.y);
  }

  function addHouseRecolorFilter(defs, id, paperHex) {
    const rgb = hexToRgb(paperHex) || { r: 0, g: 0, b: 0 };
    const filter = el("filter", { id, "color-interpolation-filters": "sRGB" }, defs);
    el(
      "feColorMatrix",
      {
        type: "matrix",
        values: [
          `0 0 0 0 ${(rgb.r / 255).toFixed(4)}`,
          `0 0 0 0 ${(rgb.g / 255).toFixed(4)}`,
          `0 0 0 0 ${(rgb.b / 255).toFixed(4)}`,
          "0 0 0 1 0",
        ].join(" "),
      },
      filter
    );
  }

  function drawSignBoldRing(svg, ascendantLongitude, ink) {
    for (let i = 0; i < 12; i++) {
      const a = polarSvg(i * 30 + signGapDeg, signBoldRadius, ascendantLongitude);
      const b = polarSvg((i + 1) * 30 - signGapDeg, signBoldRadius, ascendantLongitude);
      el(
        "path",
        {
          d: `M ${a.x} ${a.y} A ${signBoldRadius} ${signBoldRadius} 0 0 0 ${b.x} ${b.y}`,
          fill: "none",
          stroke: ink,
          "stroke-width": signBoldStroke,
          "stroke-linecap": "butt",
        },
        svg
      );
    }
  }

  function drawHouseBand(svg, chartData, ascendantLongitude, ink, hubInk) {
    circleMpl(svg, center.x, center.y, houseRingOuter, {
      fill: "none",
      stroke: ink,
      "stroke-width": 1.05,
    });
    circleMpl(svg, center.x, center.y, houseRingInner, {
      fill: "none",
      stroke: ink,
      "stroke-width": 1.05,
    });

    for (let deg = 0; deg < 360; deg++) {
      const angle = longitudeToAngle(deg, ascendantLongitude);
      const p1 = angleToPosition(angle, houseRingInner);
      const p2 = angleToPosition(angle, houseRingOuter);
      lineMpl(
        svg,
        p1.x + center.x,
        p1.y + center.y,
        p2.x + center.x,
        p2.y + center.y,
        {
          stroke: ink,
          "stroke-width": 0.55,
          "stroke-opacity": 0.78,
        }
      );
    }

    for (const houseData of chartData.house_cusps || []) {
      const angle = longitudeToAngle(houseData.longitude, ascendantLongitude);
      const p1 = angleToPosition(angle, houseRingInner);
      const p2 = angleToPosition(angle, houseRingOuter);
      lineMpl(
        svg,
        p1.x + center.x,
        p1.y + center.y,
        p2.x + center.x,
        p2.y + center.y,
        { stroke: ink, "stroke-width": 3.05 }
      );
    }

    circleMpl(svg, center.x, center.y, aspectHubRadius, {
      fill: "none",
      stroke: hubInk || ink,
      "stroke-width": 0.95,
      "stroke-opacity": 0.55,
    });
  }

  function drawCenterDisc(svg, fill) {
    if (!fill) return;
    circleMpl(svg, center.x, center.y, skyClipRadius, {
      fill,
      stroke: "none",
    });
  }

  function placeSkyImage(parent, href, p, pad) {
    el(
      "image",
      {
        href,
        x: p.x - skyClipRadius - pad,
        y: p.y - skyClipRadius - pad,
        width: (skyClipRadius + pad) * 2,
        height: (skyClipRadius + pad) * 2,
        preserveAspectRatio: "xMidYMid slice",
      },
      parent
    );
  }

  function resolveSkyPayload(chartData, opts) {
    if (opts.sky === false) return null;
    if (opts.sky && typeof opts.sky === "object") return opts.sky;
    if (chartData && chartData.sky && typeof chartData.sky === "object") {
      return chartData.sky;
    }
    return null;
  }

  function appendSvgMarkup(parent, markup) {
    if (!markup || !parent) return;
    const dummy = document.createElementNS(NS, "svg");
    dummy.innerHTML = markup;
    while (dummy.firstChild) parent.appendChild(dummy.firstChild);
  }

  function escapeXml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;");
  }

  function skyScaleGroup(parent, sky, p) {
    const size = (sky.view && sky.view.size) || 1000;
    const s = ((skyClipRadius + skyPad) * 2) / size;
    const x0 = p.x - skyClipRadius - skyPad;
    const y0 = p.y - skyClipRadius - skyPad;
    return el(
      "g",
      { transform: `translate(${x0} ${y0}) scale(${s})` },
      parent
    );
  }

  function drawDotPaths(parent, dots, maskUrl) {
    const g = el("g", maskUrl ? { mask: maskUrl } : {}, parent);
    const list = dots || [];
    for (let i = 0; i < list.length; i++) {
      const dot = list[i];
      if (!dot || !dot.d) continue;
      el(
        "path",
        {
          fill: "none",
          stroke: dot.c || "#fff",
          "stroke-width": String(dot.w),
          "stroke-opacity": String(dot.o),
          "stroke-linecap": "round",
          d: dot.d,
        },
        g
      );
    }
    return g;
  }

  function nebulaDefsMarkup(prefix, size, seed) {
    const R = size / 2;
    const blurBig = size * 0.03;
    const blurSmall = size * 0.01;
    return `
<filter id="${prefix}cloud" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="0.0021 0.0028" numOctaves="6" seed="${seed}" result="n"/>
  <feColorMatrix in="n" type="matrix" values="
      0 0 0 0 1
      0 0 0 0 1
      0 0 0 0 1
      1.35 0.55 0.30 0 -0.62"/>
  <feGaussianBlur stdDeviation="${blurSmall.toFixed(1)}"/>
</filter>
<filter id="${prefix}lane" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="0.0013 0.0034" numOctaves="5" seed="${seed + 41}" result="n"/>
  <feColorMatrix in="n" type="matrix" values="
      0 0 0 0 1
      0 0 0 0 1
      0 0 0 0 1
      1.9 0.9 0.4 0 -0.98"/>
  <feGaussianBlur stdDeviation="${(blurSmall * 0.55).toFixed(1)}"/>
</filter>
<filter id="${prefix}soft" x="-15%" y="-15%" width="130%" height="130%">
  <feGaussianBlur stdDeviation="${blurBig.toFixed(1)}"/>
</filter>
<filter id="${prefix}wash" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="0.0007" numOctaves="4" seed="${seed + 97}" result="n"/>
  <feColorMatrix in="n" type="matrix" values="
      0 0 0 0 1
      0 0 0 0 1
      0 0 0 0 1
      1.0 0.7 0.4 0 -0.55"/>
</filter>
<linearGradient id="${prefix}tint" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${size}" y2="${size}">
  <stop offset="0" stop-color="#8fa6d8"/>
  <stop offset="0.32" stop-color="#c8cfe6"/>
  <stop offset="0.58" stop-color="#e6ddc8"/>
  <stop offset="0.80" stop-color="#b8b3cf"/>
  <stop offset="1" stop-color="#7f93c4"/>
</linearGradient>
<linearGradient id="${prefix}tint2" gradientUnits="userSpaceOnUse" x1="${size}" y1="0" x2="0" y2="${size}">
  <stop offset="0" stop-color="#6f86bd"/>
  <stop offset="0.45" stop-color="#a89ec0"/>
  <stop offset="1" stop-color="#7d95c9"/>
</linearGradient>
<clipPath id="${prefix}disc"><circle cx="${R}" cy="${R}" r="${R}"/></clipPath>
<radialGradient id="${prefix}rimgrad" gradientUnits="userSpaceOnUse" cx="${R}" cy="${R}" r="${R}">
  <stop offset="0.00" stop-color="#fff"/>
  <stop offset="0.62" stop-color="#fff"/>
  <stop offset="0.88" stop-color="#8a8a8a"/>
  <stop offset="1.00" stop-color="#000"/>
</radialGradient>
<mask id="${prefix}rim"><rect width="${size}" height="${size}" fill="url(#${prefix}rimgrad)"/></mask>
<radialGradient id="${prefix}rimgrad2" gradientUnits="userSpaceOnUse" cx="${R}" cy="${R}" r="${R}">
  <stop offset="0.00" stop-color="#fff"/>
  <stop offset="0.80" stop-color="#fff"/>
  <stop offset="0.96" stop-color="#c8c8c8"/>
  <stop offset="1.00" stop-color="#3a3a3a"/>
</radialGradient>
<mask id="${prefix}rimsoft"><rect width="${size}" height="${size}" fill="url(#${prefix}rimgrad2)"/></mask>
<mask id="${prefix}cloudmask">
  <rect width="${size}" height="${size}" filter="url(#${prefix}cloud)" fill="#fff"/>
</mask>
<mask id="${prefix}lanemask">
  <rect width="${size}" height="${size}" fill="#fff"/>
  <rect width="${size}" height="${size}" filter="url(#${prefix}lane)" fill="#000"/>
</mask>
<mask id="${prefix}washmask">
  <rect width="${size}" height="${size}" filter="url(#${prefix}wash)" fill="#fff"/>
</mask>`;
  }

  function nebulaBandMasksMarkup(prefix, size, pathD) {
    const R = size / 2;
    const d = escapeXml(pathD || "M0 0Z");
    return `
<mask id="${prefix}band">
  <g filter="url(#${prefix}soft)"><path d="${d}" fill="#fff"/></g>
</mask>
<mask id="${prefix}bandtight">
  <g filter="url(#${prefix}soft)" transform="translate(${R} ${R}) scale(0.93) translate(${-R} ${-R})">
    <path d="${d}" fill="#fff"/>
  </g>
</mask>`;
  }

  function nebulaBodyMarkup(prefix, size, opacity) {
    const op = opacity == null ? 0.55 : opacity;
    return `
<g clip-path="url(#${prefix}disc)" mask="url(#${prefix}rim)">
  <g opacity="0.07" mask="url(#${prefix}washmask)">
    <rect width="${size}" height="${size}" fill="url(#${prefix}tint2)"/>
  </g>
  <g opacity="${op.toFixed(2)}" mask="url(#${prefix}band)">
    <g mask="url(#${prefix}cloudmask)">
      <g mask="url(#${prefix}lanemask)">
        <rect width="${size}" height="${size}" fill="url(#${prefix}tint)" opacity="0.58"/>
      </g>
    </g>
  </g>
  <g opacity="0.22" mask="url(#${prefix}bandtight)">
    <g mask="url(#${prefix}cloudmask)">
      <rect width="${size}" height="${size}" fill="url(#${prefix}tint)" opacity="0.5"/>
    </g>
  </g>
</g>`;
  }

  function drawSkyFromPayload(svg, defs, sky, opts, invertSky, usePlates) {
    const uid = opts._uid;
    const prefix = `psky${uid}-`;
    const clipId = `poster-sky-clip-${uid}`;
    const size = (sky.view && sky.view.size) || 1000;
    const meta = sky.meta || {};
    const seed =
      meta.nebula_seed != null
        ? Number(meta.nebula_seed)
        : Math.floor(Number(meta.mc_longitude) || 0) % 90 + 2;
    const nebulaOpacity = opts.nebula == null ? 0.55 : Number(opts.nebula);
    const pathD = (sky.milkyway && sky.milkyway.path) || "";

    appendSvgMarkup(defs, nebulaDefsMarkup(prefix, size, seed));
    if (pathD) appendSvgMarkup(defs, nebulaBandMasksMarkup(prefix, size, pathD));

    const clip = el("clipPath", { id: clipId }, defs);
    circleMpl(clip, center.x, center.y, skyClipRadius, {});

    const skyGroup = el(
      "g",
      {
        "clip-path": `url(#${clipId})`,
        style: invertSky ? "filter:invert(1)" : "",
        "data-sky-source": "json",
      },
      svg
    );
    const p = toSvg(center.x, center.y);

    // Keep feTurbulence on a static group. Transforming a live filter during
    // parallax re-rasterises the clouds every frame on some mobile engines.
    // Bright-star (near) and faint-star (far) plates still move; the eye
    // tracks those. The band is infinitely far, so leaving it still is correct.
    if (pathD && nebulaOpacity > 0) {
      const nebulaShift = el(
        "g",
        { class: "poster-sky-nebula", "data-sky-plate": "nebula" },
        skyGroup
      );
      const nebulaInner = skyScaleGroup(nebulaShift, sky, p);
      appendSvgMarkup(nebulaInner, nebulaBodyMarkup(prefix, size, nebulaOpacity));
    }

    const farDots = (sky.layers && sky.layers.far && sky.layers.far.dots) || [];
    const nearDots = (sky.layers && sky.layers.near && sky.layers.near.dots) || [];
    const maskUrl = `url(#${prefix}rimsoft)`;
    const layers = [];

    function addPlate(plate, depth, dots) {
      const shift = el(
        "g",
        {
          class: "poster-sky-shift",
          "data-sky-plate": plate,
          "data-sky-depth": String(depth),
        },
        skyGroup
      );
      const inner = skyScaleGroup(shift, sky, p);
      drawDotPaths(inner, dots, maskUrl);
      layers.push({ el: shift, depth });
    }

    if (usePlates) {
      const farDepth =
        sky.layers && sky.layers.far && sky.layers.far.depth != null
          ? sky.layers.far.depth
          : skyFarDepth;
      const nearDepth =
        sky.layers && sky.layers.near && sky.layers.near.depth != null
          ? sky.layers.near.depth
          : skyNearDepth;
      addPlate("far", farDepth, farDots);
      addPlate("near", nearDepth, nearDots);
    } else {
      addPlate("all", 1, farDots.concat(nearDots));
    }
    return layers;
  }

  function drawSky(svg, defs, chartData, opts, invertSky, usePlates) {
    if (opts.showSky === false) return [];
    const payload = resolveSkyPayload(chartData, opts);
    if (payload) {
      return drawSkyFromPayload(svg, defs, payload, opts, invertSky, usePlates);
    }
    const idx = resolveSkyIndex(chartData, opts.skySign);
    const base = String(
      opts.skyBaseUrl ||
        "https://images.plumastro.com/birthcharticons/SKYFIELDS/"
    ).replace(/\/?$/, "/");
    const uid = opts._uid;
    const clipId = `poster-sky-clip-${uid}`;

    const clip = el("clipPath", { id: clipId }, defs);
    circleMpl(clip, center.x, center.y, skyClipRadius, {});

    const plates = usePlates
      ? [
          { plate: "far", depth: skyFarDepth },
          { plate: "near", depth: skyNearDepth },
        ]
      : [{ plate: "", depth: 1 }];

    const skyGroup = el(
      "g",
      {
        "clip-path": `url(#${clipId})`,
        style: invertSky ? "filter:invert(1)" : "",
        "data-sky-file": skyFileName(idx, plates[0].plate),
      },
      svg
    );
    const p = toSvg(center.x, center.y);
    const layers = [];
    for (let i = 0; i < plates.length; i++) {
      const spec = plates[i];
      const file = skyFileName(idx, spec.plate);
      const shift = el(
        "g",
        {
          class: "poster-sky-shift",
          "data-sky-plate": spec.plate || "all",
          "data-sky-depth": String(spec.depth),
        },
        skyGroup
      );
      placeSkyImage(shift, base + file, p, skyPad);
      layers.push({ el: shift, depth: spec.depth });
    }
    return layers;
  }

  function drawAspects(svg, chartData, ascendantLongitude, ink, opts) {
    const orb = opts.aspectOrb == null ? 8 : Number(opts.aspectOrb);
    const bodies = collectBodies(chartData, opts.includeAngleAspects);
    const aspects = computePosterAspects(bodies, orb);
    const pp = chartData.planet_positions || {};
    const angles = chartData.angles || {};

    function hubPos(body) {
      if (pp[body]) return planetGeo(pp[body].longitude, ascendantLongitude).hub;
      if (angles[body]) return planetGeo(angles[body].longitude, ascendantLongitude).hub;
      return null;
    }

    for (const asp of aspects) {
      const p1 = hubPos(asp.body1);
      const p2 = hubPos(asp.body2);
      if (!p1 || !p2) continue;
      const attrs = {
        stroke: ink,
        "stroke-width": 1.85,
        "stroke-opacity": 0.92,
        "stroke-linecap": "round",
        fill: "none",
      };
      if (asp.dashed) {
        attrs["stroke-dasharray"] = "7 6";
        attrs["stroke-opacity"] = 0.82;
      }
      lineMpl(svg, p1.x, p1.y, p2.x, p2.y, attrs);
    }
  }

  function drawHouseDiscs(
    vector,
    glyphs,
    chartData,
    ascendantLongitude,
    iconsBaseUrl,
    cacheBust,
    ink,
    paper,
    interactive
  ) {
    const houseCusps = chartData.house_cusps || [];
    for (let i = 0; i < houseCusps.length; i++) {
      const houseData = houseCusps[i];
      const houseNumber = String(houseData.house_number);
      let currentCusp = houseData.longitude;
      let nextCusp = houseCusps[(i + 1) % 12].longitude;
      if (nextCusp < currentCusp) nextCusp += 360;
      let centerAngle = (currentCusp + nextCusp) / 2;
      if (centerAngle >= 360) centerAngle -= 360;

      const angle = longitudeToAngle(centerAngle, ascendantLongitude);
      const p = angleToPosition(angle, houseIconRadius);
      const x = p.x + center.x;
      const y = p.y + center.y;
      circleMpl(vector, x, y, houseDiscRadius, {
        fill: isLightColor(paper) ? ink : "#ffffff",
        stroke: "none",
      });
      placeIcon(
        glyphs,
        iconsBaseUrl,
        houseNumber,
        x,
        y,
        houseIconSize,
        cacheBust,
        true,
        interactive,
        houseDiscRadius + 8,
        "poster-hit-house"
      );
    }
  }

  function drawZodiacGlyphs(
    glyphs,
    ascendantLongitude,
    signIconsBaseUrl,
    cacheBust,
    interactive
  ) {
    for (let i = 0; i < SIGNS.length; i++) {
      const angle = longitudeToAngle(i * 30 + 15, ascendantLongitude);
      const p = angleToPosition(angle, signGlyphRadius);
      const hit = placeIcon(
        glyphs,
        signIconsBaseUrl,
        SIGNS[i],
        p.x + center.x,
        p.y + center.y,
        signIconSize,
        cacheBust,
        true,
        interactive,
        signIconSize * 0.55,
        "poster-hit-sign"
      );
      if (interactive && hit) {
        el(
          "path",
          {
            class: "hit-area",
            d: annulusSectorPath(
              i * 30,
              (i + 1) * 30,
              signBoldRadius - 10,
              signGlyphRadius + signIconSize * 0.55,
              ascendantLongitude
            ),
            fill: "#fff",
            "fill-opacity": 0,
          },
          hit
        );
      }
    }
  }

  function drawBodies(
    vector,
    glyphs,
    chartData,
    ascendantLongitude,
    iconsBaseUrl,
    cacheBust,
    ink,
    interactive
  ) {
    const planetPositions = chartData.planet_positions || {};
    const angles = chartData.angles || {};
    const offsets = computeIconAngleOffsets(
      planetPositions,
      ascendantLongitude,
      angles
    );

    function drawOne(name, longitude) {
      const geo = planetGeo(longitude, ascendantLongitude);
      lineMpl(vector, geo.on.x, geo.on.y, geo.tick.x, geo.tick.y, {
        stroke: ink,
        "stroke-width": 2.55,
        "stroke-linecap": "butt",
      });

      const dth = offsets[name] || 0;
      const iconAngle = geo.angle + dth;
      const ip = angleToPosition(iconAngle, planetIconRadius);
      const ix = ip.x + center.x;
      const iy = ip.y + center.y;
      if (Math.abs(dth) > 0.012) {
        lineMpl(vector, geo.tick.x, geo.tick.y, ix, iy, {
          stroke: ink,
          "stroke-width": 0.7,
          "stroke-opacity": 0.4,
        });
      }

      const size =
        name === "Ascendant" || name === "MC" ? angleIconSize : planetIconSize;
      placeIcon(
        glyphs,
        iconsBaseUrl,
        name,
        ix,
        iy,
        size,
        cacheBust,
        false,
        interactive,
        size * 0.62,
        "poster-hit-planet"
      );

      circleMpl(vector, geo.hub.x, geo.hub.y, nodeRadiusPx, {
        fill: ink,
        stroke: "none",
        "fill-opacity": 0.9,
      });
    }

    for (const [planet, data] of Object.entries(planetPositions)) {
      drawOne(planet, data.longitude);
    }
    for (const [angleName, data] of Object.entries(angles)) {
      drawOne(angleName, data.longitude);
    }
  }

  function recolorInlineSvg(svgText, color) {
    return svgText
      .replace(/\s(width|height)="[^"]*"/g, "")
      .replace(/fill="#000000"/gi, `fill="${color}"`)
      .replace(/fill="#000"/gi, `fill="${color}"`)
      .replace(/fill="black"/gi, `fill="${color}"`);
  }

  async function inlineGlyphImages(glyphLayer, ink) {
    const images = Array.from(glyphLayer.querySelectorAll("image"));
    const cache = new Map();
    let replaced = 0;
    for (const img of images) {
      const href = img.getAttribute("href") || img.getAttributeNS(XLINK, "href");
      if (!href) continue;
      let text = cache.get(href);
      if (text === undefined) {
        try {
          const res = await fetch(href);
          if (!res.ok) throw new Error(String(res.status));
          text = await res.text();
        } catch (e) {
          cache.set(href, null);
          continue;
        }
        cache.set(href, text);
      }
      if (!text) continue;
      const x = parseFloat(img.getAttribute("x"));
      const y = parseFloat(img.getAttribute("y"));
      const w = parseFloat(img.getAttribute("width"));
      const h = parseFloat(img.getAttribute("height"));
      const transform = img.getAttribute("transform") || "";
      const wrapper = el("g", {
        transform,
        "data-icon": img.getAttribute("data-icon") || "",
      });
      const inner = el("g", {});
      const colored = recolorInlineSvg(text, ink);
      const parsed = new DOMParser().parseFromString(colored, "image/svg+xml");
      const root = parsed.documentElement;
      if (!root || root.tagName.toLowerCase() !== "svg") continue;
      const vb = (root.getAttribute("viewBox") || "0 0 100 100").trim().split(/[\s,]+/);
      const vbW = parseFloat(vb[2]) || 100;
      const vbH = parseFloat(vb[3]) || 100;
      inner.setAttribute(
        "transform",
        `translate(${x} ${y}) scale(${w / vbW} ${h / vbH})`
      );
      while (root.firstChild) inner.appendChild(root.firstChild);
      wrapper.appendChild(inner);
      img.parentNode.replaceChild(wrapper, img);
      replaced += 1;
    }
    return replaced;
  }

  /**
   * @param {HTMLElement|string} container
   * @param {object} chartData
   * @param {object} [options]
   * @returns {SVGSVGElement}
   */
  function renderPosterBirthChart(container, chartData, options) {
    const root =
      typeof container === "string"
        ? document.querySelector(container)
        : container;
    if (!root) throw new Error("renderPosterBirthChart: container not found");
    const opts = options || {};
    const iconsBaseUrl =
      opts.iconsBaseUrl || "https://images.plumastro.com/birthcharticons";
    const signIconsBaseUrl = opts.signIconsBaseUrl || iconsBaseUrl;
    const iconsCacheBust = opts.iconsCacheBust || "";
    const mix = opts.style === "mix" || opts.mix === true;
    const ink = opts.ink || "#111111";
    const paper = opts.paper || "#ffffff";
    const innerInk = mix ? opts.innerInk || "#f4f1ea" : ink;
    const innerPaper = mix ? opts.innerPaper || "#080c16" : paper;
    const inkIsLight = mix ? false : isLightColor(ink);
    const invertSky = mix ? false : !inkIsLight;
    const interactive = opts.interactive !== false;
    const uid = ++instanceId;
    opts._uid = uid;

    root.innerHTML = "";
    const svg = el("svg", {
      viewBox: `0 0 ${canvasSize} ${canvasSize}`,
      width: "100%",
      height: "100%",
      xmlns: NS,
      style: "display:block;background:transparent;overflow:visible;",
      color: ink,
    });
    root.appendChild(svg);

    if (interactive) injectInteractiveCss(svg);

    const defs = el("defs", {}, svg);
    const houseFilterId = `poster-house-ink-${uid}`;
    addHouseRecolorFilter(defs, houseFilterId, paper);
    const vector = el("g", { "data-layer": "vector" }, svg);
    const glyphs = el(
      "g",
      {
        "data-layer": "glyphs",
        style: inkIsLight ? "filter:invert(1)" : "",
      },
      svg
    );
    const houseGlyphs = el(
      "g",
      {
        "data-layer": "house-glyphs",
        filter: `url(#${houseFilterId})`,
      },
      svg
    );

    const pp = chartData.planet_positions || {};
    const ascendantLongitude =
      pp.Ascendant != null ? pp.Ascendant.longitude : null;

    if (mix) drawCenterDisc(svg, innerPaper);
    const useSkyPlates = interactive && opts.skyParallax !== false;
    const skyLayers = drawSky(
      svg,
      defs,
      chartData,
      opts,
      invertSky,
      useSkyPlates
    );
    svg.appendChild(vector);
    svg.appendChild(glyphs);
    svg.appendChild(houseGlyphs);

    drawAspects(vector, chartData, ascendantLongitude, innerInk, opts);
    drawHouseBand(vector, chartData, ascendantLongitude, ink, innerInk);
    drawSignBoldRing(vector, ascendantLongitude, ink);
    drawHouseDiscs(
      vector,
      houseGlyphs,
      chartData,
      ascendantLongitude,
      iconsBaseUrl,
      iconsCacheBust,
      ink,
      paper,
      interactive
    );
    drawZodiacGlyphs(
      glyphs,
      ascendantLongitude,
      signIconsBaseUrl,
      iconsCacheBust,
      interactive
    );
    drawBodies(
      vector,
      glyphs,
      chartData,
      ascendantLongitude,
      iconsBaseUrl,
      iconsCacheBust,
      ink,
      interactive
    );

    if (interactive) {
      attachParallax(svg, skyLayers, opts.skyParallaxShift);
    }

    if (opts.inlineIcons) {
      inlineGlyphImages(glyphs, ink)
        .then(function (replaced) {
          if (replaced > 0) glyphs.removeAttribute("style");
        })
        .catch(function () {
          /* keep <image> glyphs; invert filter still applies when ink is light */
        });
    }

    return svg;
  }

  global.renderPosterBirthChart = renderPosterBirthChart;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { renderPosterBirthChart };
  }
})(typeof window !== "undefined" ? window : globalThis);
